import classNames from 'classnames';
import { Info } from 'lucide-react';
import { useState } from 'react';
import i18n from '@/settings/i18n';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { priceFormatSuperscript } from '@/lib/price';
import { priceFormater } from '@/settings/index';
import { getSavingsAmount } from './helpers';
import BeforeTradeIn from './BeforeTradeIn';
import PriceSavingsBadge from './PriceSavingsBadge';

const t = i18n.init('product');

const SAVINGS_BADGE_CLASS =
  'h-5 shrink-0 rounded bg-[#D5FFE2] px-1 text-xxs font-bold text-[#128452]';

const Disclaimer = () => (
  <div className="absolute bottom-6 left-0 z-10 w-screen max-w-[calc(100vw-40px)]  xs:max-w-[350px] lg:left-auto lg:right-0 lg:top-[calc(100%+5px)]">
    <div className="rounded-md border bg-white px-3 py-2 text-center text-xs font-normal leading-tight text-gray-700 shadow-lg xs:px-5 xs:py-4">
      {t`This strikethrough comparison price shows how much you would pay for a new device, based on an average of retailer prices. The product compared may vary in its version, condition, warranty period, or other minor variations such as packaging.`}
    </div>
  </div>
);

const ComparePrice = ({ compareAtPrice }: { compareAtPrice: number }) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <>
      <button
        type="button"
        className="flex items-center gap-1 leading-none"
        onClick={() => setShowDisclaimer(!showDisclaimer)}
        onBlur={() => setShowDisclaimer(false)}
      >
        <span>{t`new`}</span>
        <span className="line-through" id="e2e-pdp-price" translate="no">
          {priceFormatSuperscript(priceFormater.long(compareAtPrice))}
        </span>
        <Info className="h-3 w-3 shrink-0" />
      </button>
      {showDisclaimer && <Disclaimer />}
    </>
  );
};

const ProductPrice = ({
  alignRight = false,
  className,
}: {
  alignRight?: boolean;
  className?: string;
}) => {
  const {
    selectedSkuDetails: { compareAtPrice, finalPrice },
  } = useProductPageContext();

  const hasComparePrice =
    compareAtPrice !== null &&
    compareAtPrice > 0 &&
    finalPrice < compareAtPrice;
  const savings = getSavingsAmount(compareAtPrice, finalPrice);

  return (
    <div className={classNames('relative min-w-0', className)}>
      {finalPrice > 0 && (
        <div
          className={classNames('flex items-baseline gap-2', {
            'justify-end': alignRight,
          })}
        >
          {alignRight && <BeforeTradeIn />}
          <span className="text-xl font-bold" translate="no">
            {priceFormatSuperscript(priceFormater.long(finalPrice))}
          </span>
          {!alignRight && <BeforeTradeIn />}
        </div>
      )}
      {hasComparePrice && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-medium text-gray-700/30 lg:flex-row-reverse">
          {savings != null && (
            <PriceSavingsBadge
              savings={savings}
              className={SAVINGS_BADGE_CLASS}
            />
          )}
          <ComparePrice compareAtPrice={compareAtPrice} />
        </div>
      )}
    </div>
  );
};

export default ProductPrice;
